import axios, { AxiosResponse } from 'axios';
import { auth, err, validators } from '../utils';
import {
  type CreatePayoutAccountResponse,
  type PayoutAccount,
  PayoutProvider,
  VerificationOfPayeeAcceptance,
} from '../utils/types/Entity';
import { VerificationOfPayeeResponse } from '../utils/types/Entity/PayoutAccount.types';

const URL = '/api/payout-account';

const getPayoutAccounts = async (
  token: string,
  lang?: string,
): Promise<PayoutAccount[]> => {
  try {
    const headers = {
      ...auth.bearerAuthHeader(token),
      ...(lang ? { 'Accept-Language': lang } : {}),
    };
    const res: AxiosResponse<{ payoutAccounts: PayoutAccount[] }> =
      await axios.get(URL, { headers });
    if (res.status < 200 || res.status >= 300) {
      throw new Error(`${res.status} error`);
    }

    return res.data.payoutAccounts;
  } catch (e) {
    throw err.handleError(e, 'error getting payout accounts');
  }
};

const changeDefaultStatus = async (
  token: string,
  payoutAccountID: string,
  isDefault: boolean,
): Promise<void> => {
  try {
    const res: AxiosResponse<void> = await axios.patch(
      `${URL}/${payoutAccountID}/default?is_default=${isDefault.toString()}`,
      {},
      {
        headers: auth.bearerAuthHeader(token),
      },
    );
    if (res.status < 200 || res.status >= 300) {
      throw new Error(`${res.status} error`);
    }
  } catch (e) {
    throw err.handleError(e, 'error updating account');
  }
};

const createPayoutAccount = async (
  token: string,
  provider: PayoutProvider,
  options: Record<string, string | Record<string, string>>,
  lang?: string,
): Promise<CreatePayoutAccountResponse> => {
  try {
    const headers = {
      ...auth.bearerAuthHeader(token),
      ...(lang ? { 'Accept-Language': lang } : {}),
    };
    const res: AxiosResponse<{ payoutAccount: CreatePayoutAccountResponse }> =
      await axios.post(
        URL,
        {
          provider,
          options,
        },
        { headers },
      );

    if (res.status < 200 || res.status >= 300) {
      throw new Error(`${res.status} error`);
    }

    return res.data.payoutAccount;
  } catch (e) {
    throw err.handleError(e, 'error creating account');
  }
};

const getProviderAuthorizationURL = async (
  token: string,
  provider: PayoutProvider,
): Promise<string> => {
  try {
    const res: AxiosResponse<{ authorizationURL: string }> = await axios.get(
      `${URL}/provider/${provider.toLowerCase()}/authorization-url`,
      {
        headers: auth.bearerAuthHeader(token),
      },
    );
    if (res.status < 200 || res.status >= 300) {
      throw new Error(`${res.status} error`);
    }

    validators.stringValidator(res.data.authorizationURL, 'authorizationURL', {
      nonEmpty: true,
    });
    return res.data.authorizationURL;
  } catch (e) {
    throw err.handleError(e, 'error getting provider authorization URL');
  }
};

const acceptVerificationOfPayee = async (
  token: string,
  options: VerificationOfPayeeAcceptance,
  provider: PayoutProvider,
  currency?: string,
  lang?: string,
): Promise<VerificationOfPayeeResponse> => {
  try {
    const headers = {
      ...auth.bearerAuthHeader(token),
      ...(lang ? { 'Accept-Language': lang } : {}),
    };
    const res: AxiosResponse<{ payoutAccount: VerificationOfPayeeResponse }> =
      await axios.patch(
        URL,
        {
          options,
          provider,
          currency,
        },
        { headers },
      );

    if (res.status < 200 || res.status >= 300) {
      throw new Error(`${res.status} error`);
    }

    return res.data.payoutAccount;
  } catch (e) {
    throw err.handleError(e, 'error confirming payout account');
  }
};

const deletePayoutAccount = async (
  token: string,
  payoutAccountID: string,
  provider: string,
  providerCustomerID: string,
): Promise<void> => {
  try {
    const res: AxiosResponse<void> = await axios.patch(
      `${URL}/${payoutAccountID}/delete`,
      { provider, providerCustomerID },
      { headers: auth.bearerAuthHeader(token) },
    );
    if (res.status < 200 || res.status >= 300) {
      throw new Error(`${res.status} error`);
    }
  } catch (e) {
    throw err.handleError(e, 'error deleting account');
  }
};

const generateProviderToken = async (
  token: string,
  provider: string,
  providerCustomerID: string,
): Promise<{ token: string }> => {
  try {
    const res: AxiosResponse<{ token: string }> = await axios.post(
      `${URL}/provider/token`,
      {
        provider,
        options: {
          providerCustomerID,
        },
      },
      { headers: auth.bearerAuthHeader(token) },
    );
    if (res.status < 200 || res.status >= 300) {
      throw new Error(`${res.status} error`);
    }
    return res.data;
  } catch (e) {
    throw err.handleError(e, 'error deleting account');
  }
};

const getPayoutAccountByProvider = async (
  token: string,
  provider: PayoutProvider,
  requireValid: boolean,
  lang?: string,
): Promise<PayoutAccount | null> => {
  try {
    const headers = {
      ...auth.bearerAuthHeader(token),
      ...(lang ? { 'Accept-Language': lang } : {}),
    };
    const res: AxiosResponse<PayoutAccount | null> = await axios.get(
      `${URL}/provider/${provider.toLowerCase()}?require_valid=${requireValid.toString()}`,
      {
        headers,
      },
    );
    if (res.status < 200 || res.status >= 300) {
      throw new Error(`${res.status} error`);
    }
    return res.data;
  } catch (e) {
    throw err.handleError(e, 'error getting account by provider');
  }
};

export default {
  getPayoutAccounts,
  changeDefaultStatus,
  createPayoutAccount,
  getProviderAuthorizationURL,
  acceptVerificationOfPayee,
  deletePayoutAccount,
  generateProviderToken,
  getPayoutAccountByProvider,
};
